import React, { useState, useEffect } from 'react';
import { MagnifyingGlassIcon, FunnelIcon, PlusIcon } from '@heroicons/react/24/outline';
import { staffApi } from '../staffApi';
import TutorList from './components/TutorList';
import TutorDetail from './components/TutorDetail';
import CreateTutor from './components/CreateTutor';

export default function Tutors() {
  const [tutors, setTutors] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedTutor, setSelectedTutor] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);

  useEffect(() => {
    loadTutors();
    loadSubjects();
  }, []);

  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      loadTutors();
    }, 300);

    return () => clearTimeout(delayedSearch);
  }, [searchTerm, selectedSubject]);

  const loadTutors = async () => {
    try {
      setLoading(true);
      const params = {};
      if (searchTerm) params.search = searchTerm;
      if (selectedSubject) params.subject = selectedSubject;
      
      const response = await staffApi.getTutors(params);
      setTutors(response.data.tutors || []);
      setError(null);
    } catch (err) {
      setError('Failed to load tutors');
      console.error('Error loading tutors:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadSubjects = async () => {
    try {
      const response = await staffApi.getSubjects();
      setSubjects(response.data.data || []);
    } catch (err) {
      console.error('Error loading subjects:', err);
      setSubjects([]); // Set empty array on error to prevent map error
    }
  };

  const handleTutorClick = async (tutorId) => {
    try {
      setLoading(true);
      const response = await staffApi.getTutorById(tutorId);
      setSelectedTutor(response.data);
      setShowDetail(true);
    } catch (err) {
      setError('Failed to load tutor details');
      console.error('Error loading tutor details:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToList = () => {
    setShowDetail(false);
    setSelectedTutor(null);
    setShowCreateForm(false);
  };

  const handleCreateTutor = async (tutorData) => {
    // Refresh tutor list after creation
    await loadTutors();
    setShowCreateForm(false);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSubjectChange = (e) => {
    setSelectedSubject(e.target.value);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedSubject('');
  };

  if (showCreateForm) {
    return (
      <CreateTutor 
        onCreateTutor={handleCreateTutor}
        onBack={handleBackToList}
      />
    );
  }

  if (showDetail && selectedTutor) {
    return (
      <TutorDetail 
        tutor={selectedTutor} 
        onBack={handleBackToList}
        onSendEmail={async (subject, message) => {
          try {
            await staffApi.sendEmailToTutor(selectedTutor.id, subject, message);
            alert('Email sent successfully!');
          } catch (err) {
            alert('Failed to send email');
            console.error('Error sending email:', err);
          }
        }}
        onSendWhatsApp={async (message) => {
          try {
            await staffApi.sendWhatsAppToTutor(selectedTutor.id, message);
            alert('WhatsApp message sent successfully!');
          } catch (err) {
            alert('Failed to send WhatsApp message');
            console.error('Error sending WhatsApp:', err);
          }
        }}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="min-w-0 flex-1">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:truncate sm:text-3xl sm:tracking-tight">
            Tutors
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Manage and view tutor information
          </p>
        </div>
        <div className="mt-4 flex md:mt-0 md:ml-4">
          <button
            onClick={() => setShowCreateForm(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Add New Tutor
          </button>
        </div>
      </div>

      {/* Search and Filter Section */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {/* Search Input */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
            </div>
            <input
              type="text"
              placeholder="Search tutors by name, email, or subject..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            />
          </div>

          {/* Subject Filter */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FunnelIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
            </div>
            <select
              value={selectedSubject}
              onChange={handleSubjectChange}
              className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            >
              <option value="">All Subjects</option>
              {(subjects || []).map((subject) => (
                <option key={subject.id} value={subject.name}>
                  {subject.name}
                </option>
              ))}
            </select>
          </div>

          {/* Clear Filters Button */}
          <div className="flex items-center">
            <button
              onClick={clearFilters}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Clear Filters
            </button>
          </div>
        </div>

        {/* Active Filters Display */}
        {(searchTerm || selectedSubject) && (
          <div className="mt-4 flex flex-wrap gap-2">
            {searchTerm && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                Search: "{searchTerm}"
                <button
                  onClick={() => setSearchTerm('')}
                  className="ml-2 inline-flex items-center justify-center w-4 h-4 rounded-full text-indigo-400 hover:bg-indigo-200 hover:text-indigo-500"
                >
                  ×
                </button>
              </span>
            )}
            {selectedSubject && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                Subject: {selectedSubject}
                <button
                  onClick={() => setSelectedSubject('')}
                  className="ml-2 inline-flex items-center justify-center w-4 h-4 rounded-full text-green-400 hover:bg-green-200 hover:text-green-500"
                >
                  ×
                </button>
              </span>
            )}
          </div>
        )}
      </div>

      {/* Results Section */}
      <div className="bg-white shadow rounded-lg">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            <span className="ml-2 text-gray-600">Loading tutors...</span>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <div className="text-red-600 mb-4">{error}</div>
            <button
              onClick={loadTutors}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Try Again
            </button>
          </div>
        ) : (
          <TutorList 
            tutors={tutors} 
            onTutorClick={handleTutorClick}
            loading={loading}
          />
        )}
      </div>
    </div>
  );
}

